// EditEmployeeModal.js
import React, { useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  TimePicker,
  message,
  Radio,
} from "antd";
import dayjs from "dayjs";
import { useUnit, useStoreMap } from "effector-react";
import {
  $chosenUser,
  changeChosenUserEv,
  sendEditUserFx,
} from "../../models/userListModel";
import { $userGroups } from "../../models/userModel/stores";

import styles from "./EmployeeUserList.module.css";

const { Option } = Select;

export default function EditEmployeeModal({ visible, onClose }) {
  const [form] = Form.useForm();
  const chosenUser = useUnit($chosenUser);
  const changeChosenUser = useUnit(changeChosenUserEv);
  const sendEditUser = useUnit(sendEditUserFx);
  const pending = useUnit(sendEditUserFx.pending);
  const userGroups = useStoreMap({
    store: $userGroups,
    keys: [],
    fn: (groups) => groups || [],
  });

  const isAzag = userGroups.includes("Azag");

  const parsedDOB = dayjs(
    chosenUser?.date_of_birth,
    ["D.M.YYYY", "DD.MM.YYYY", "YYYY-MM-DD"],
    true
  );

  useEffect(() => {
    if (chosenUser) {
      form.setFieldsValue({
        user_name: chosenUser?.user_name || "",
        user_surname: chosenUser?.user_surname || "",
        emp_department: chosenUser?.emp_department || "",
        emp_position: chosenUser?.emp_position || "",
        emp_category: chosenUser?.emp_category || "",
        user_gender: chosenUser?.user_gender || "",
        user_mobile: chosenUser?.user_mobile || "",
        user_date_of_birth: parsedDOB.isValid() ? parsedDOB : null,
        start_work_time: chosenUser?.start_work_time
          ? dayjs(chosenUser.start_work_time, "HH:mm")
          : null,
        end_work_time: chosenUser?.end_work_time
          ? dayjs(chosenUser.end_work_time, "HH:mm")
          : null,
        ...(isAzag && {
          license_plate: chosenUser?.license_plate || "",
          emp_type: chosenUser?.emp_type || "",
        }),
      });
    }
  }, [chosenUser]);

  const handleSubmit = (values) => {
    const placeId = chosenUser.place_id || chosenUser.sort_key;

    const payload = {
      action: "update",
      values: {
        user_id: chosenUser.user_id || chosenUser.partition_key,
        place_id: placeId,
        user_name: values.user_name,
        user_surname: values.user_surname,
        emp_department: values.emp_department,
        emp_position: values.emp_position,
        emp_category: values.emp_category,
        user_gender: values.user_gender,
        user_mobile: values.user_mobile,
        user_date_of_birth: values.user_date_of_birth
          ? dayjs(values.user_date_of_birth).format("DD.MM.YYYY")
          : "",
        start_work_time: values.start_work_time?.format("HH:mm") || "",
        end_work_time: values.end_work_time?.format("HH:mm") || "",
        data_url: chosenUser.presigned_url ? [chosenUser.presigned_url] : [],
        // license_plate: values.license_plate,
        // emp_type: values.emp_type,
        ...(isAzag && {
          license_plate: values.license_plate,
          emp_type: values.emp_type,
        }),
      },
    };

    sendEditUser(payload).then(() => {
      message.success("Данные успешно обновлены");
      handleClose();
    });
  };

  const handleClose = () => {
    changeChosenUser(null);
    onClose();
  };

  return (
    <Modal
      title="Редактировать данные сотрудника"
      open={visible && !!chosenUser}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      centered
      width={700}
      bodyStyle={{ maxHeight: "85vh", overflowY: "auto" }}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <div className={styles.gridForm}>
          <Form.Item name="user_name" label="Имя" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="user_surname"
            label="Фамилия"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="emp_department" label="Департамент">
            <Input />
          </Form.Item>
          <Form.Item name="emp_position" label="Должность">
            <Input />
          </Form.Item>

          <Form.Item name="emp_category" label="Статус">
            <Select>
              <Option value="Сотрудник">Сотрудник</Option>
              <Option value="Уволен">Уволен</Option>
            </Select>
          </Form.Item>

          {isAzag && (
            <>
              <Form.Item name="license_plate" label="Гос. номер машины">
                <Input />
              </Form.Item>
              <Form.Item name="emp_type" label="Тип сотрудника">
                <Select>
                  <Option value="Штатный">Штатный</Option>
                  <Option value="Наемный">Наемный</Option>
                </Select>
              </Form.Item>
            </>
          )}

          <Form.Item name="start_work_time" label="Начало рабочего дня">
            <TimePicker
              format="HH:mm"
              minuteStep={15}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item name="end_work_time" label="Конец рабочего дня">
            <TimePicker
              format="HH:mm"
              minuteStep={15}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item name="user_gender" label="Пол">
            <Radio.Group>
              <Radio value="Male">М</Radio>
              <Radio value="Female">Ж</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item name="user_date_of_birth" label="Дата рождения">
            <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="user_mobile" label="Телефон">
            <Input />
          </Form.Item>
        </div>

        <div style={{ marginTop: 24 }}>
          <Button type="primary" htmlType="submit" loading={pending}>
            Сохранить
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
